import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { Link as ReactRouterLink } from 'react-router';

import isNaN from 'lodash/isNaN';

import { languageCodeToName } from 'js/lib/language';

import { Link, Typography2 } from '@coursera/cds-core';

import { itemsPerPage, root } from 'bundles/directory-graphql/common/constants';
import { generateHrefWithPreview } from 'bundles/directory-graphql/common/utils';
import LinksContainer from 'bundles/directory-graphql/components/LinksContainer';
import Metatags from 'bundles/directory-graphql/components/MetatagsWithPagination';
import NotFound from 'bundles/directory-graphql/components/NotFound';
import { useArticlesDirectoryUrlData } from 'bundles/directory-graphql/hooks/useArticlesDirectoryUrlData';
import { useArticlesSubfolderData } from 'bundles/directory-graphql/hooks/useArticlesSubfolderData';
import { supportedSubfolderLocaleCodes } from 'bundles/internationalization-lib/constants/subfolderLocaleCodes';
import PaginationControls from 'bundles/page/components/cds/PaginationControls';
import { useIntl } from 'bundles/page/lib/useIntl';

import _t from 'i18n!nls/directory-graphql';

const ArticlesMetatags = ({ total }: { total: number }) => {
  const additionalSettings = {
    title: _t('Articles Directory'),
    description: _t('The Coursera Articles directory includes links to all of our articles.'),
    disableCrawlerIndexing: true,
    useFollowForDisablingIndexing: true,
  };
  return <Metatags total={total} additionalSettings={additionalSettings} />;
};

const SubfolderLink = ({ localeCode }: { localeCode: string }) => {
  const intl = useIntl();
  const { pathname, isPreview } = useArticlesDirectoryUrlData();
  const { total } = useArticlesSubfolderData({ localeCode, isPreview });

  const title = intl.formatMessage(_t(`{count} {count, plural, =1 {Article} other {Articles}} in {language}`), {
    count: total || 0,
    language: languageCodeToName(localeCode),
  });

  const href = generateHrefWithPreview(`${pathname}?localeCode=${localeCode}`, isPreview);

  return (
    <Typography2 component="li">
      <Link typographyVariant="bodySecondary" href={href} variant="quiet">
        {title}
      </Link>
    </Typography2>
  );
};

const ArticlesSubfolderCategories = () => {
  const allSubfolderLocaleCodes = ['en-US', ...supportedSubfolderLocaleCodes];

  return (
    <React.Fragment>
      <ul>
        {allSubfolderLocaleCodes.map((localeCode) => {
          return <SubfolderLink localeCode={localeCode} key={localeCode} />;
        })}
      </ul>
      <ArticlesMetatags total={allSubfolderLocaleCodes.length} />
    </React.Fragment>
  );
};

const ArticlesSubfolderLinks = ({ localeCodeFromQuery }: { localeCodeFromQuery: string }) => {
  const { pageNumber, isPreview } = useArticlesDirectoryUrlData();

  const { articles, total = 0 } = useArticlesSubfolderData({
    localeCode: localeCodeFromQuery,
    currentPage: pageNumber,
    isPreview,
  });

  const pageCount = Math.ceil(total / itemsPerPage);

  const returnBackHref = generateHrefWithPreview(`${root}/articles`, isPreview);

  const returnBackMessage = (
    <FormattedMessage
      message={_t(`Popular articles in {locale}. Click {linkBack} to go back.`)}
      locale={languageCodeToName(localeCodeFromQuery)}
      linkBack={
        <Link href={returnBackHref} component={ReactRouterLink}>
          {_t('here')}
        </Link>
      }
    />
  );

  if (isNaN(pageNumber) || pageNumber < 1 || pageNumber > pageCount) {
    return (
      <React.Fragment>
        {returnBackMessage}
        <NotFound />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      {returnBackMessage}
      <LinksContainer titledLinks={articles} />
      <PaginationControls currentPage={pageNumber} largeStyle pageCount={pageCount} maxPages={4} renderLinks={true} />
      <ArticlesMetatags total={total} />
    </React.Fragment>
  );
};

export const ArticlesDirectoryContentSubfolders = () => {
  const { localeCodeFromQuery } = useArticlesDirectoryUrlData();

  return (
    <div>
      {!localeCodeFromQuery ? (
        <ArticlesSubfolderCategories />
      ) : (
        <ArticlesSubfolderLinks localeCodeFromQuery={localeCodeFromQuery} />
      )}
    </div>
  );
};

export const ArticlesDirectoryContent = () => {
  return <ArticlesDirectoryContentSubfolders />;
};
